import {
  Scene,
  WebGLRenderer,
  PerspectiveCamera,
  Mesh,
  Vector2,
  MeshBasicMaterial,
  Layers,
  Color,
  Material,
  ShaderMaterial
} from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { FXAAShader } from "three/addons/shaders/FXAAShader.js";

import {
  BLOOM_SCENE,
  BloomParams,
  BlendParams,
  BloomEffectConfig,
  RenderMode,
  CustomShaderPass
} from "../type";
import {
  bloomShader,
  bloomOnlyShader,
  baseOnlyShader,
  getBlendModeValue
} from "../shaders/bloom.shaders";
import {
  getDefaultBloomParams,
  getDefaultBlendParams
} from "../util";

export class BloomEffect {
  private scene: Scene;
  private renderer: WebGLRenderer;
  private camera: PerspectiveCamera;
  private width: number;
  private height: number;

  // 블룸 효과 관련 속성
  private bloomLayer: Layers;
  private darkMaterial: MeshBasicMaterial;
  private materials: Record<string, Material> = {};

  // 컴포저들
  private bloomComposer!: EffectComposer; // 생성자에서 초기화됨
  private finalComposer!: EffectComposer; // 생성자에서 초기화됨
  private bloomPass!: UnrealBloomPass; // 생성자에서 초기화됨

  // 설정값들
  private bloomParams: BloomParams;
  private blendParams: BlendParams;
  private originalFragmentShader: string;
  private currentRenderMode: RenderMode = 'full';

  constructor(
    scene: Scene,
    renderer: WebGLRenderer,
    camera: PerspectiveCamera,
    width: number,
    height: number,
    config: BloomEffectConfig
  ) {
    this.scene = scene;
    this.renderer = renderer;
    this.camera = camera;
    this.width = width;
    this.height = height;
    this.bloomParams = { ...getDefaultBloomParams(), ...config.bloomParams };
    this.blendParams = { ...getDefaultBlendParams(), ...config.blendParams };

    // 블룸 레이어 초기화
    this.bloomLayer = new Layers();
    this.bloomLayer.set(BLOOM_SCENE);

    // 블룸 효과가 적용되지 않을 객체를 위한 재질
    this.darkMaterial = new MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 1.0,
      depthWrite: false
    });

    this.originalFragmentShader = bloomShader.fragmentShader;
    this.initialize();
  }

  private initialize() {
    // 기본 렌더 패스
    const renderScene = new RenderPass(this.scene, this.camera);

    // 블룸 패스 생성 - 해상도 75%로 설정하여 품질 개선
    this.bloomPass = new UnrealBloomPass(
      new Vector2(this.width * 0.75, this.height * 0.75),
      this.bloomParams.strength!,
      this.bloomParams.radius!,
      this.bloomParams.threshold!
    );

    // 블룸 컴포저 생성
    this.bloomComposer = new EffectComposer(this.renderer);
    this.bloomComposer.renderToScreen = false;
    this.bloomComposer.addPass(renderScene);
    this.bloomComposer.addPass(this.bloomPass);

    // 블룸 컴포저 크기 설정 (고품질을 위해 실제 해상도 사용)
    this.bloomComposer.setSize(this.width, this.height);

    // 블룸 합성 셰이더 설정
    const bloomShaderCopy = {
      uniforms: {
        ...bloomShader.uniforms,
        bloomTexture: { value: this.bloomComposer.renderTarget2.texture },
        bloomStrength: { value: this.blendParams.bloomStrength },
        blendOpacity: { value: this.blendParams.blendOpacity },
        applyGamma: { value: this.blendParams.applyGamma ? 1.0 : 0.0 },
        blendMode: { value: getBlendModeValue(this.blendParams.blendMode!) }
      },
      vertexShader: bloomShader.vertexShader,
      fragmentShader: bloomShader.fragmentShader
    };

    // 블룸 합성 패스
    const mixPass = new ShaderPass(
      new ShaderMaterial({
        uniforms: bloomShaderCopy.uniforms,
        vertexShader: bloomShaderCopy.vertexShader,
        fragmentShader: bloomShaderCopy.fragmentShader,
        defines: {}
      }), 'baseTexture'
    );
    mixPass.needsSwap = true;

    // FXAA 안티앨리어싱 패스
    const fxaaPass = new ShaderPass(FXAAShader);

    // 최종 컴포저 생성
    this.finalComposer = new EffectComposer(this.renderer);
    this.finalComposer.addPass(renderScene);
    this.finalComposer.addPass(mixPass);
    this.finalComposer.addPass(fxaaPass);

    // 최종 출력 패스 (원본에서 누락됨)
    const outputPass = new OutputPass();
    this.finalComposer.addPass(outputPass);

    // 최종 컴포저 크기 설정 (고품질을 위해 실제 해상도 사용)
    this.finalComposer.setSize(this.width, this.height);
  }

  // 블룸 레이어에 속하지 않은 객체들의 재질을 임시로 어둡게 변경
  private darkenNonBloomed() {
    this.scene.traverse((obj) => {
      if (obj instanceof Mesh && !this.bloomLayer.test(obj.layers)) {
        this.materials[obj.uuid] = obj.material;
        obj.material = this.darkMaterial;
      }
    });
  }

  // 재질을 원래대로 복원
  private restoreMaterial() {
    this.scene.traverse((obj) => {
      if (obj instanceof Mesh && this.materials[obj.uuid]) {
        obj.material = this.materials[obj.uuid];
        delete this.materials[obj.uuid];
      }
    });
  }

  // 블룸 효과 렌더링
  render() {
    if (this.currentRenderMode === 'full') {
      this.darkenNonBloomed();
      this.bloomComposer.render();
      this.restoreMaterial();
      this.finalComposer.render();
    } else if (this.currentRenderMode === 'bloom') {
      this.darkenNonBloomed();
      this.bloomComposer.render();
      this.restoreMaterial();
      this.finalComposer.render();
    } else {
      this.finalComposer.render();
    }
  }

  // 크기 조정
  resize(width: number, height: number) {
    this.width = width;
    this.height = height;

    // 픽셀 비율 계산 (원본과 동일)
    const pixelRatio = Math.min(window.devicePixelRatio, 2);

    if (this.bloomComposer) {
      this.bloomComposer.setSize(width, height);
    }
    if (this.finalComposer) {
      this.finalComposer.setSize(width, height);
    }
    // 블룸 패스 해상도 업데이트 - 품질 개선을 위해 0.75배로 설정
    if (this.bloomPass) {
      this.bloomPass.resolution.set(width * 0.75, height * 0.75);
    }

    // FXAA 해상도 업데이트 (고품질 설정)
    const fxaaPass = this.finalComposer.passes.find(pass =>
      pass instanceof ShaderPass &&
      (pass as CustomShaderPass).material &&
      (pass as CustomShaderPass).material.uniforms &&
      (pass as CustomShaderPass).material.uniforms["resolution"]
    ) as CustomShaderPass | undefined;

    if (fxaaPass) {
      // 더 정밀한 FXAA 설정을 위해 실제 렌더링 해상도 사용
      const effectiveWidth = width * pixelRatio;
      const effectiveHeight = height * pixelRatio;
      fxaaPass.material.uniforms["resolution"].value.x = 1 / effectiveWidth;
      fxaaPass.material.uniforms["resolution"].value.y = 1 / effectiveHeight;
    }
  }

  // 블룸 파라미터 업데이트
  updateBloomParams(params: Partial<BloomParams>) {
    this.bloomParams = { ...this.bloomParams, ...params };

    if (this.bloomPass) {
      if (params.strength !== undefined) {
        this.bloomPass.strength = params.strength;
      }
      if (params.radius !== undefined) {
        this.bloomPass.radius = params.radius;
      }
      if (params.threshold !== undefined) {
        this.bloomPass.threshold = params.threshold;
      }
    }
  }

  // 블렌드 파라미터 업데이트
  updateBlendParams(params: Partial<BlendParams>) {
    this.blendParams = { ...this.blendParams, ...params };

    const mixPass = this.finalComposer.passes.find(pass =>
      pass instanceof ShaderPass &&
      (pass as CustomShaderPass).material &&
      (pass as CustomShaderPass).material.uniforms &&
      (pass as CustomShaderPass).material.uniforms['blendOpacity']
    ) as CustomShaderPass | undefined;

    if (mixPass) {
      if (params.blendOpacity !== undefined) {
        mixPass.material.uniforms['blendOpacity'].value = params.blendOpacity;
      }
      if (params.bloomStrength !== undefined) {
        mixPass.material.uniforms['bloomStrength'].value = params.bloomStrength;
      }
      if (params.applyGamma !== undefined) {
        mixPass.material.uniforms['applyGamma'].value = params.applyGamma ? 1.0 : 0.0;
      }
      if (params.blendMode !== undefined) {
        mixPass.material.uniforms['blendMode'].value = getBlendModeValue(params.blendMode);
      }
    }
  }

  // 렌더 모드 변경
  setRenderMode(mode: RenderMode) {
    if (this.currentRenderMode === mode) return;

    this.currentRenderMode = mode;

    const mixPass = this.finalComposer.passes.find(pass =>
      pass instanceof ShaderPass &&
      (pass as CustomShaderPass).material &&
      (pass as CustomShaderPass).material.uniforms &&
      (pass as CustomShaderPass).material.uniforms['bloomTexture']
    ) as CustomShaderPass | undefined;

    if (!mixPass) {
      console.error('❌ 블룸 합성 패스를 찾을 수 없습니다.');
      return;
    }

    if (mode === 'bloom') {
      mixPass.material.fragmentShader = bloomOnlyShader;
    } else if (mode === 'base') {
      mixPass.material.fragmentShader = baseOnlyShader;
    } else {
      mixPass.material.fragmentShader = this.originalFragmentShader;
    }

    mixPass.material.needsUpdate = true;
  }

  // 블룸 객체 하이라이트 (디버깅용)
  highlightBloomedObjects() {
    this.scene.traverse((obj) => {
      if (obj instanceof Mesh && this.bloomLayer.test(obj.layers)) {
        const originalMaterial = obj.material;
        const tempMaterial = originalMaterial.clone();

        tempMaterial.emissive = new Color(0x00ff00);
        tempMaterial.emissiveIntensity = 1;

        obj.material = tempMaterial;

        setTimeout(() => {
          obj.material = originalMaterial;
          tempMaterial.dispose();
        }, 3000);
      }
    });
  }

  // 현재 설정값들 getter
  getBloomParams(): BloomParams {
    return { ...this.bloomParams };
  }

  getBlendParams(): BlendParams {
    return { ...this.blendParams };
  }

  getCurrentRenderMode(): RenderMode {
    return this.currentRenderMode;
  }

  // 리소스 정리
  dispose() {
    if (this.bloomComposer) {
      this.bloomComposer.dispose();
    }
    if (this.finalComposer) {
      this.finalComposer.dispose();
    }
    if (this.darkMaterial) {
      this.darkMaterial.dispose();
    }
  }
} 